import client from '@/support/Client';
import { InstanceLoader } from '@/support/InstanceLoader';
import { studlyCase } from '@/support/String';
import store from '@/store';
import { isNullOrUndefined, isObject } from 'util';
export class Model {
    constructor(attributes = {}) {
        this.meta = {};
        // $name must be exactly the same as the class name.
        this.$name = 'Model';
        this.$endpoint = '';
        this.$primaryKey = 'id';
        this.$fillable = [];
        this.$includes = [];
        this.filters = {};
        this.$page = null;
        this.$limit = null;
        this.$sort = {};
        Object.assign(this, attributes);
    }
    getFilterLabel() {
        if (this.name !== undefined) {
            return this.name;
        }
        return 'UNKNOWN';
    }
    exists() {
        return !isNullOrUndefined(this[this.$primaryKey]);
    }
    first() {
        return this.limit(1)
            .all()
            .then((models) => {
            if (models === undefined || !Array.isArray(models) || !(models[0] instanceof Model)) {
                return Promise.reject(models);
            }
            Object.assign(this, models[0]);
            return Promise.resolve(models[0]);
        }, (error) => {
            return Promise.reject(error);
        });
    }
    all() {
        const url = this.$endpoint;
        return this.request('get', url);
    }
    find(id) {
        const url = this.$endpoint + '/' + id;
        return this.request('get', url);
    }
    create(attributes) {
        const url = this.$endpoint;
        attributes = this.getPayload(attributes);
        return this.request('post', url, attributes);
    }
    put(attributes) {
        const url = this.$endpoint + '/' + this.resolvePrimaryKey();
        return this.request('put', url, this.getPayload(attributes));
    }
    update(attributes) {
        const url = this.$endpoint + '/' + this.resolvePrimaryKey();
        return this.request('patch', url, this.getPayload(attributes));
    }
    delete(attributes) {
        const url = this.$endpoint + '/' + this.resolvePrimaryKey();
        return this.request('delete', url, attributes);
    }
    deleteMany(ids) {
        const url = this.$endpoint;
        return this.request('delete', url, { ids });
    }
    getInstanceName() {
        return this.$name;
    }
    resolvePrimaryKey() {
        return this[this.$primaryKey];
    }
    getPayload(attributes) {
        if (attributes === undefined) {
            attributes = { ...{}, ...this };
            const keys = Object.keys(attributes);
            keys.forEach((property, index) => {
                if (!this.isFillable(property)) {
                    delete attributes[property];
                }
            });
        }
        return attributes;
    }
    request(method, url, payload = {}, hasPdf) {
        if (this.$includes.length) {
            payload.with = this.$includes;
        }
        const keys = Object.keys(this.filters);
        if (keys.length) {
            keys.forEach((key) => {
                const model = this.filters[key];
                if (isObject(model) && typeof model.resolvePrimaryKey === 'function') {
                    this.filters[key] = model.resolvePrimaryKey();
                }
            });
            payload.filters = this.filters;
        }
        if (this.$page !== null) {
            payload.page = this.$page;
        }
        if (this.$limit !== null) {
            payload.limit = this.$limit;
        }
        if (this.$sort.key) {
            payload.sort = this.$sort.order === 'DESC' ? `!${this.$sort.key}` : this.$sort.key;
        }
        return client(method, url, payload, hasPdf).then((response) => {
            return hasPdf ? Promise.resolve(response) : Promise.resolve(this.responseToModel(response));
        }, (error) => {
            if (error.response !== undefined && error.response.status === 401) {
                store.dispatch('userDeauthenticated');
            }
            return Promise.reject(error);
        });
    }
    include(includes) {
        if (typeof includes === 'string') {
            includes = [includes];
        }
        this.$includes = includes;
        return this;
    }
    filter(filters, value) {
        if (typeof filters === 'string') {
            const key = filters;
            filters = {};
            filters[key] = value ? value : null;
        }
        this.filters = { ...this.filters, ...filters };
        return this;
    }
    page(page) {
        this.$page = page;
        return this;
    }
    limit(limit) {
        this.$limit = limit;
        return this;
    }
    isFillable(key) {
        return this.$fillable.includes(key);
    }
    sort(key, order = 'ASC') {
        this.$sort = { key, order };
        return this;
    }
    dataToModel(data, className, rows = false) {
        if (rows && Array.isArray(rows)) {
            const meta = {
                current_page: data.current_page || 1,
                first_page: data.from || 1,
                last_page: data.last_page || 1,
                total: data.total || 1,
            };
            rows.forEach((row, i) => {
                rows[i] = this.rowToModel(row, className);
                const currentMeta = rows[i].meta !== undefined && typeof rows[i].meta === 'object' ? rows[i].meta : {};
                rows[i].meta = { ...meta, ...currentMeta };
            });
            return rows;
        }
        return this.rowToModel(data, className);
    }
    responseToModel(response) {
        const paginatedResults = response.data.data !== undefined && Array.isArray(response.data.data);
        const data = response.data;
        const rows = paginatedResults ? response.data.data : false;
        const className = this.getInstanceName();
        return this.dataToModel(data, className, rows);
    }
    rowToModel(row, modelClass, includes = []) {
        const attributes = row;
        const model = InstanceLoader.get(modelClass, attributes);
        Object.keys(model).forEach((key) => {
            const setterMethod = 'set' + studlyCase(key) + 'Attribute';
            if (model.__proto__.hasOwnProperty(setterMethod)) {
                model[key] = model[setterMethod](model[key]);
            }
        });
        if (row && row.id) {
            model.id = row.id;
        }
        return model;
    }
    clone() {
        return Object.assign(Object.create(Object.getPrototypeOf(this)), this);
    }
}
export var GroupIncludes;
(function (GroupIncludes) {
    GroupIncludes["AUDIT"] = "audit";
    GroupIncludes["CHAT"] = "chat";
    GroupIncludes["USERS"] = "users";
    GroupIncludes["SUBJECTS"] = "subjects";
    GroupIncludes["ROLES"] = "roles";
    GroupIncludes["PARENT"] = "parent";
    GroupIncludes["NOTES"] = "notes";
    GroupIncludes["PROJECTS"] = "projects";
    GroupIncludes["TYPE"] = "type";
    GroupIncludes["SUB_GROUPS"] = "sub_groups";
    GroupIncludes["LEVEL"] = "level";
})(GroupIncludes || (GroupIncludes = {}));
